import React, { useState, useEffect } from 'react';
import { 
  advertisingSpaces, 
  getAvailableSpaces, 
  getSpacesByBlogPost,
  getPricingTiers,
  getAdRecommendations,
  getPerformanceAnalytics,
  updateAllPrices
} from '../data/advertisingSpaces';
import './AdvertisingPage.css';

export default function AdvertisingPage() {
  const [selectedPackage, setSelectedPackage] = useState('starter');
  const [contactForm, setContactForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    budget: '',
    advertiserType: '',
    targetAudience: '',
    message: ''
  });
  const [adFiles, setAdFiles] = useState({
    bannerAd: null,
    logo: null,
    content: null
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [selectedSpaces, setSelectedSpaces] = useState([]);
  const [showSpacesModal, setShowSpacesModal] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [currentPrices, setCurrentPrices] = useState([]);

  useEffect(() => {
    // Update prices and get analytics
    const updatedSpaces = updateAllPrices();
    setCurrentPrices(updatedSpaces);
    setAnalytics(getPerformanceAnalytics());
  }, []);

  const advertisingPackages = getPricingTiers();

  const advertiserTypes = [
    { value: 'travel-agency', label: 'Travel Agency' },
    { value: 'hotel-chain', label: 'Hotel Chain' },
    { value: 'tour-operator', label: 'Tour Operator' },
    { value: 'travel-tech', label: 'Travel Technology' },
    { value: 'budget-travel', label: 'Budget Travel' },
    { value: 'luxury-travel', label: 'Luxury Travel' },
    { value: 'adventure-travel', label: 'Adventure Travel' },
    { value: 'other', label: 'Other' }
  ];

  const targetAudiences = [
    'travel-hotspots',
    'adventure-travel',
    'culture-art',
    'budget-travel',
    'travel-safety',
    'digital-nomad',
    'activity-holidays'
  ];

  const handleFileUpload = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      setAdFiles(prev => ({
        ...prev,
        [fileType]: file
      }));
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    // Generate recommendations based on form data
    const recs = getAdRecommendations(
      contactForm.advertiserType,
      contactForm.budget ? parseInt(contactForm.budget) : null,
      contactForm.targetAudience
    );
    setRecommendations(recs);
    setShowSpacesModal(true);
  };

  const handlePayment = () => {
    if (!acceptedTerms) {
      alert('Please accept the terms and conditions');
      return;
    }
    setShowPaymentModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSpaceSelection = (spaceId) => {
    setSelectedSpaces(prev => 
      prev.includes(spaceId) 
        ? prev.filter(id => id !== spaceId)
        : [...prev, spaceId]
    );
  };

  const getSelectedSpacesTotal = () => {
    return selectedSpaces.reduce((total, spaceId) => {
      const space = currentPrices.find(s => s.id === spaceId);
      return total + (space ? space.currentPrice : 0);
    }, 0);
  };

  const getTopPerformingSpaces = () => {
    return currentPrices
      .filter(space => space.status === 'available')
      .sort((a, b) => b.performanceScore - a.performanceScore)
      .slice(0, 6);
  };

  return (
    <div className="advertising-page">
      <div className="container">
        {/* Header Section */}
        <div className="advertising-header">
          <h1>Advertise with GradedWash</h1>
          <p>Reach engaged travelers and travel enthusiasts through our premium advertising platform</p>
          
          {analytics && (
            <div className="analytics-overview">
              <div className="analytics-card">
                <h3>{analytics.totalViews.toLocaleString()}</h3>
                <p>Monthly Views</p>
              </div>
              <div className="analytics-card">
                <h3>{analytics.avgCTR}%</h3>
                <p>Average CTR</p>
              </div>
              <div className="analytics-card">
                <h3>{analytics.availableSpaces}</h3>
                <p>Available Spaces</p>
              </div>
              <div className="analytics-card">
                <h3>£{analytics.potentialRevenue.toLocaleString()}</h3>
                <p>Revenue Potential</p>
              </div>
            </div>
          )}
        </div>

        {/* Pricing Packages */}
        <section className="pricing-section">
          <h2>Advertising Packages</h2>
          <div className="pricing-grid">
            {Object.entries(advertisingPackages).map(([key, pkg]) => (
              <div key={key} className={`pricing-card ${selectedPackage === key ? 'selected' : ''}`}>
                <div className="package-header">
                  <h3>{pkg.name}</h3>
                  <div className="price">
                    <span className="amount">{pkg.price}</span>
                    <span className="duration">{pkg.duration}</span>
                  </div>
                </div>
                <p className="package-description">{pkg.description}</p>
                <ul className="package-features">
                  {pkg.features.map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))}
                </ul>
                <button
                  onClick={() => setSelectedPackage(key)}
                  className={`btn ${selectedPackage === key ? 'btn--primary' : 'btn--secondary'}`}
                >
                  {selectedPackage === key ? 'Selected' : 'Choose Package'}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Top Performing Spaces */}
        <section className="spaces-section">
          <h2>Top Performing Ad Spaces</h2>
          <div className="spaces-grid">
            {getTopPerformingSpaces().map(space => (
              <div key={space.id} className="space-card">
                <div className="space-header">
                  <h4>{space.description}</h4>
                  <span className={`status ${space.status}`}>{space.status}</span>
                </div>
                <div className="space-details">
                  <p><strong>Location:</strong> {space.location}</p>
                  <p><strong>Size:</strong> {space.size}</p>
                  <p><strong>Views:</strong> {space.viewsPerMonth.toLocaleString()}/month</p>
                  <p><strong>CTR:</strong> {(space.ctr * 100).toFixed(1)}%</p>
                  <p><strong>Performance:</strong> {space.performanceScore}/10</p>
                </div>
                <div className="space-pricing">
                  <div className="price-info">
                    <span className="current-price">£{space.currentPrice}</span>
                    <span className="price-period">/week</span>
                  </div>
                  <button
                    onClick={() => handleSpaceSelection(space.id)}
                    className={`btn ${selectedSpaces.includes(space.id) ? 'btn--primary' : 'btn--secondary'}`}
                  >
                    {selectedSpaces.includes(space.id) ? 'Selected' : 'Select Space'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="contact-section">
          <h2>Get Started with Advertising</h2>
          <form onSubmit={handleContactSubmit} className="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Contact Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={contactForm.name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="company">Company Name *</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={contactForm.company}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={contactForm.email}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={contactForm.phone}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="advertiserType">Advertiser Type *</label>
                <select
                  id="advertiserType"
                  name="advertiserType"
                  value={contactForm.advertiserType}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                >
                  <option value="">Select advertiser type</option>
                  {advertiserTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="budget">Monthly Budget (£)</label>
                <input
                  type="number"
                  id="budget"
                  name="budget"
                  value={contactForm.budget}
                  onChange={handleInputChange}
                  placeholder="e.g., 1000"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="targetAudience">Target Audience</label>
              <select
                id="targetAudience"
                name="targetAudience"
                value={contactForm.targetAudience}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="">Select target audience</option>
                {targetAudiences.map(audience => (
                  <option key={audience} value={audience}>
                    {audience.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="message">Campaign Details</label>
              <textarea
                id="message"
                name="message"
                value={contactForm.message}
                onChange={handleInputChange}
                rows="4"
                placeholder="Tell us about your campaign goals, target audience, and any specific requirements..."
                className="form-input"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn--primary">
                Get Recommendations
              </button>
            </div>
          </form>
        </section>

        {/* Recommendations Modal */}
        {showSpacesModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Recommended Ad Spaces</h3>
                <button onClick={() => setShowSpacesModal(false)} className="close-btn">×</button>
              </div>
              
              <div className="recommendations-list">
                {recommendations.map((rec, index) => (
                  <div key={rec.id} className="recommendation-card">
                    <div className="recommendation-header">
                      <h4>{rec.description}</h4>
                      <span className="performance-score">{rec.performanceScore}/10</span>
                    </div>
                    <p className="recommendation-reason">{rec.recommendationReason}</p>
                    <div className="recommendation-stats">
                      <span>Views: {rec.viewsPerMonth.toLocaleString()}/month</span>
                      <span>CTR: {(rec.ctr * 100).toFixed(1)}%</span>
                      <span>Est. ROI: {rec.estimatedROI}%</span>
                    </div>
                    <div className="recommendation-pricing">
                      <span className="price">£{rec.currentPrice}/week</span>
                      <button
                        onClick={() => handleSpaceSelection(rec.id)}
                        className={`btn ${selectedSpaces.includes(rec.id) ? 'btn--primary' : 'btn--secondary'}`}
                      >
                        {selectedSpaces.includes(rec.id) ? 'Selected' : 'Select'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {selectedSpaces.length > 0 && (
                <div className="selected-summary">
                  <h4>Selected Spaces: {selectedSpaces.length}</h4>
                  <p>Total Cost: £{getSelectedSpacesTotal()}/week</p>
                  <button onClick={handlePayment} className="btn btn--primary">
                    Proceed to Payment
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Complete Your Booking</h3>
                <button onClick={() => setShowPaymentModal(false)} className="close-btn">×</button>
              </div>
              
              <div className="payment-summary">
                <h4>Booking Summary</h4>
                <div className="summary-details">
                                     <p><strong>Package:</strong> {advertisingPackages[selectedPackage]?.name}</p>
                  <p><strong>Selected Spaces:</strong> {selectedSpaces.length}</p>
                  <p><strong>Weekly Cost:</strong> £{getSelectedSpacesTotal()}</p>
                  <p><strong>Monthly Cost:</strong> £{getSelectedSpacesTotal() * 4}</p>
                </div>
                
                <div className="terms-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                    />
                    I agree to the terms and conditions
                  </label>
                </div>
                
                <div className="payment-actions">
                  <button onClick={() => setShowPaymentModal(false)} className="btn btn--secondary">
                    Cancel
                  </button>
                  <button 
                    onClick={handlePayment} 
                    disabled={!acceptedTerms}
                    className="btn btn--primary"
                  >
                    Complete Payment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 